import { CircleInfoIcon } from "@xola/icons";
import { Alert, Input, Select, SlideDown, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DurationUnit, getDuration } from "common";
import dayjs from "dayjs";
import { isEmpty, isNil } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useState } from "react";
import { SecondaryButton } from "../Common/SecondaryButton";

interface ExperienceDurationFieldProps {
    isDisabled?: boolean;
    name: string;
    label: string;
    originalValue: number;
    value: number;
    error?: string;
    tooltip?: string;
    onChange: (value: number) => void;
}

const MIN_DURATION_IN_MINUTES_FOR_WARNING = 24 * 60; /* days * hours * minutes */

export const ExperienceDurationField = ({
    value: minutes,
    name,
    label,
    tooltip,
    error,
    isDisabled = false,
    originalValue,
    onChange,
}: ExperienceDurationFieldProps) => {
    const [duration, setDuration] = useState(getDuration(minutes, DurationUnit.MINUTE));

    const [value, setValue] = useState<number | null>(duration.value);
    const [unit, setUnit] = useState(duration.unit);
    const [shouldShowWarning, setShouldShowWarning] = useState(false);

    const originalDuration = getDuration(originalValue, DurationUnit.MINUTE);

    useEffect(() => {
        if (!isNil(value) && unit) {
            const valueInMin = dayjs.duration({ [unit]: value }).asMinutes();
            if (valueInMin !== minutes) {
                onChange(valueInMin);
            }
        }
    }, [value, unit, minutes, onChange]);

    useEffect(() => {
        const updatedDuration = getDuration(minutes, DurationUnit.MINUTE);
        const updatedDurationInMinutes = dayjs.duration({ [updatedDuration.unit]: updatedDuration.value }).asMinutes();
        if (
            originalValue > 0 &&
            updatedDurationInMinutes > 0 &&
            Math.abs(updatedDurationInMinutes - originalValue) > MIN_DURATION_IN_MINUTES_FOR_WARNING
        ) {
            setShouldShowWarning(true);
        } else {
            setShouldShowWarning(false);
        }
    }, [minutes, originalValue]);

    const handleChangeUnit = (e: any) => {
        const { value: currentUnit } = e.target;
        setUnit(currentUnit);
        applyChanges(currentUnit, value ?? 0);
    };

    const handleChangeValue = (e: any) => {
        const value = isEmpty(e.target.value) ? null : e.target.value;
        setValue(value);
        applyChanges(unit, value);
    };

    const applyChanges = (unit: DurationUnit, value: number) => {
        const newDurationMinutes = dayjs.duration({ [unit]: value }).asMinutes();
        if (
            originalValue > 0 &&
            newDurationMinutes > 0 &&
            Math.abs(newDurationMinutes - minutes) > MIN_DURATION_IN_MINUTES_FOR_WARNING
        ) {
            setShouldShowWarning(true);
        } else {
            setShouldShowWarning(false);
        }

        setDuration(getDuration(newDurationMinutes, DurationUnit.MINUTE));
        onChange(newDurationMinutes);
    };

    const handleConvertDuration = () => {
        applyChanges(originalDuration.unit, originalDuration.value);
        setUnit(originalDuration.unit);
        setValue(originalDuration.value);
        setShouldShowWarning(false);
    };

    return (
        <div>
            <div>
                <span className={clsx("mr-1 text-sm font-bold", error ? "text-danger" : "text-black")}>
                    {label} {error}
                </span>
                {tooltip && !error ? (
                    <Tooltip content={tooltip} placement="bottom">
                        <CircleInfoIcon />
                    </Tooltip>
                ) : null}
            </div>
            <div className="mt-1 flex">
                <Input
                    className="h-10 flex-grow rounded-tr-none rounded-br-none border-r-0"
                    name={name}
                    disabled={isDisabled}
                    type="number"
                    min={1}
                    value={value ? value : null}
                    onChange={handleChangeValue}
                />
                <Select
                    value={unit}
                    disabled={isDisabled}
                    className="h-10 max-w-25 flex-shrink rounded-tl-none rounded-bl-none"
                    data-testid="experience-duration-field-duration-unit"
                    onChange={handleChangeUnit}
                >
                    <option value={DurationUnit.MINUTE}>{DurationUnit.MINUTE}</option>
                    <option value={DurationUnit.HOUR}>{DurationUnit.HOUR}</option>
                    <option value={DurationUnit.DAY}>{DurationUnit.DAY}</option>
                </Select>
            </div>

            <SlideDown isOpen={shouldShowWarning}>
                <div className="my-6">
                    <Alert color="warning">
                        <div className="flex-0 flex flex-col space-y-2">
                            <span>
                                Your updated {label.toLowerCase()} is significantly different from the original{" "}
                                {label.toLowerCase()} of{" "}
                                <b>
                                    <var data-var="originalDuration">{originalDuration.value}</var>&nbsp;
                                    {pluralize(originalDuration.unit, originalDuration.value)}
                                </b>
                                . Please double check for accuracy.
                            </span>
                            <SecondaryButton size="small" className="self-end" onClick={handleConvertDuration}>
                                Convert to&nbsp;<var data-var="originalDuration">{originalDuration.value}</var>&nbsp;
                                {pluralize(originalDuration.unit, originalDuration.value)}
                            </SecondaryButton>
                        </div>
                    </Alert>
                </div>
            </SlideDown>
        </div>
    );
};
